import React, { useState, useEffect } from "react";
import {
  X,
  AlertCircle,
} from "lucide-react";
import {
  backgroundOptions,
  fontOptions,
} from "../../utils/postConstants";
import NovelForm from "./NovelForm";
import ScriptForm from "./ScriptForm";

const PostForm = ({
  // State values
  postType,
  title,
  content,
  selectedFont,
  selectedColor,
  backgroundOption,
  tags,
  currentTag,
  currentChapter,
  chapterTitles,
  chapterContents,
  currentScene,
  sceneTitles,
  sceneContents,

  // State setters (actions)
  setTitle,
  setContent,
  setSelectedFont,
  setSelectedColor,
  setBackgroundOption,
  setCurrentTag,
  setCurrentChapter,
  setChapterTitle,
  setChapterContent,
  setCurrentScene,
  setSceneTitle,
  setSceneContent,

  // Helper functions
  handleTagKeyDown,
  handleContentKeyDown,
  removeTag,
  getCurrentLineInfo,

  // Refs
  contentRef,
}) => {
  // State for modals
  const [showFontModal, setShowFontModal] = useState(false);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  
  // State for cursor position to show line info
  const [cursorPosition, setCursorPosition] = useState(0);
  const [lineInfo, setLineInfo] = useState({
    currentLine: "",
    lineLength: 0,
    maxLength: 42,
  });

  // Update line info when content or cursor position changes
  useEffect(() => {
    // CHANGED: Added check for Story, Book, and Script types
    if (getCurrentLineInfo && contentRef.current && postType !== "Novel" && postType !== "Story" && postType !== "Book" && postType !== "Script") {
      const info = getCurrentLineInfo(content, cursorPosition);
      setLineInfo(info);
    }
  }, [content, cursorPosition, getCurrentLineInfo, postType, contentRef]); // ADDED contentRef here

  // Handle cursor position changes
  const handleCursorPositionChange = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;

    setContent(newValue);
    setCursorPosition(newCursorPos);
  };

  // If Novel type, render NovelForm instead
  const isChapterType = ["Novel", "Story", "Book"].includes(postType);
  
  // ADD: Check for scene-based types (Script)
  const isSceneType = ["Script"].includes(postType);

  if (isChapterType || isSceneType) {
    return (
      <>
        <div className="mb-4 px-4 sm:px-0 sm:mb-6">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
            Styling Options
          </label>
          <div className="flex items-center gap-2">
            {/* Font Selector */}
            <button
              type="button"
              onClick={() => setShowFontModal(!showFontModal)}
              className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-all"
              title="Font Style"
            >
              <span
                className={`text-xl sm:text-2xl font-bold text-gray-800 ${selectedFont}`}
              >
                A
              </span>
            </button>

            {/* Background Selector */}
            <button
              type="button"
              onClick={() => setShowBackgroundModal(!showBackgroundModal)}
              className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-all"
              title="Background"
            >
              <div
                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-gray-400 ${
                  backgroundOptions[backgroundOption]?.class || "bg-white"
                }`}
              />
            </button>

            {/* Color Selector */}
            <button
              type="button"
              onClick={() => setShowColorModal(!showColorModal)}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-gray-300 rounded-lg transition-all ${
                selectedColor === "white" ? "bg-black" : "bg-white"
              }`}
              title="Text Color"
            >
              <span
                className={`text-xl sm:text-2xl font-bold ${selectedFont} ${
                  selectedColor === "white" ? "text-white" : "text-black"
                }`}
              >
                A
              </span>
            </button>
          </div>
        </div>

        {showFontModal && (
          <div
            className="fixed inset-0 z-50"
            onClick={() => setShowFontModal(false)}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" />
            <div
              className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-lg w-full ${
                window.innerWidth < 640
                  ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                  : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
                <h3 className="text-lg font-semibold text-gray-800">
                  Select Font Style
                </h3>
                <button
                  onClick={() => setShowFontModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              <div
                className={`${
                  window.innerWidth < 640
                    ? "max-h-[calc(40vh-70px)]"
                    : "max-h-[calc(60vh-80px)]"
                } overflow-y-auto`}
              >
                <div className="p-1">
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-1">
                    {fontOptions.map((font) => (
                      <button
                        key={font.class}
                        onClick={() => {
                          setSelectedFont(font.class);
                          setShowFontModal(false);
                        }}
                        className={`flex flex-col items-center p-3 rounded-lg transition-all relative ${
                          selectedFont === font.class
                            ? "bg-gray-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`text-3xl sm:text-4xl font-bold text-gray-800 mb-2 ${font.class}`}
                        >
                          A
                        </span>
                        <span className="text-xs text-gray-600 text-center">
                          {font.name}
                        </span>
                        {selectedFont === font.class && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {showBackgroundModal && (
          <div
            className="fixed inset-0 z-50"
            onClick={() => setShowBackgroundModal(false)}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" />
            <div
              className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-2xl w-full ${
                window.innerWidth < 640
                  ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                  : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
                <h3 className="text-lg font-semibold text-gray-800">
                  Choose Background
                </h3>
                <button
                  onClick={() => setShowBackgroundModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              <div
                className={`${
                  window.innerWidth < 640
                    ? "max-h-[calc(40vh-70px)]"
                    : "max-h-[calc(60vh-80px)]"
                } overflow-y-auto`}
              >
                <div className="p-1">
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-1">
                    {Object.entries(backgroundOptions).map(([key, option]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setBackgroundOption(key);
                          setShowBackgroundModal(false);
                        }}
                        className={`flex flex-col items-center p-3 rounded-lg transition-all relative ${
                          backgroundOption === key
                            ? "bg-gray-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-gray-300 mb-2 ${option.class}`}
                        >
                          {key === "none" && (
                            <div className="h-full w-full rounded-full flex items-center justify-center text-lg text-gray-600 font-bold">
                              ×
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-gray-600 text-center leading-tight">
                          {option.label}
                        </span>
                        {backgroundOption === key && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showColorModal && (
          <div
            className="fixed inset-0 z-50"
            onClick={() => setShowColorModal(false)}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" />
            <div
              className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-lg w-full ${
                window.innerWidth < 640
                  ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                  : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
                <h3 className="text-lg font-semibold text-gray-800">
                  Select Text Color
                </h3>
                <button
                  onClick={() => setShowColorModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              <div
                className={`${
                  window.innerWidth < 640
                    ? "max-h-[calc(40vh-70px)]"
                    : "max-h-[calc(60vh-80px)]"
                } overflow-y-auto`}
              >
                <div className="p-1">
                  <div className="grid grid-cols-2 gap-1">
                    {[
                      { value: "black", label: "Black" },
                      { value: "white", label: "White" },
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => {
                          setSelectedColor(value);
                          setShowColorModal(false);
                        }}
                        className={`flex flex-col items-center p-4 rounded-lg transition-all relative ${
                          selectedColor === value
                            ? "bg-gray-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`w-12 h-12 ${
                            value === "black" ? "bg-black" : "bg-white"
                          } rounded-full border-2 border-gray-300 mb-2`}
                        ></div>
                        <span className="text-xs text-gray-600 text-center">
                          {label}
                        </span>
                        {selectedColor === value && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Novel/Book/Story-specific form */}
        {isChapterType && (
          <NovelForm
            title={title}
            setTitle={setTitle}
            currentChapter={currentChapter}
            setCurrentChapter={setCurrentChapter}
            chapterTitles={chapterTitles}
            chapterContents={chapterContents}
            setChapterTitle={setChapterTitle}
            setChapterContent={setChapterContent}
            selectedFont={selectedFont}
            selectedColor={selectedColor}
            backgroundOption={backgroundOption}
            backgroundOptions={backgroundOptions}
          />
        )}

        {/* Script-specific form */}
        {isSceneType && (
          <ScriptForm
            title={title}
            setTitle={setTitle}
            currentScene={currentScene}
            setCurrentScene={setCurrentScene}
            sceneTitles={sceneTitles}
            sceneContents={sceneContents}
            setSceneTitle={setSceneTitle}
            setSceneContent={setSceneContent}
            selectedFont={selectedFont}
            selectedColor={selectedColor}
            backgroundOption={backgroundOption}
            backgroundOptions={backgroundOptions}
          />
        )}

        {/* Tags for novels */}
        <div className="mb-4 px-4 sm:px-0 sm:mb-6">
          <div className="sm:hidden">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs border border-gray-300 flex-shrink-0"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}

                {tags.length < 5 && (
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Add tag..."
                    className="flex-1 min-w-[100px] p-2 text-sm bg-transparent border-0 focus:outline-none placeholder-gray-400"
                    maxLength={20}
                  />
                )}
              </div>

              {tags.length >= 5 && (
                <div className="text-xs text-gray-500 px-1">
                  Maximum 5 tags reached
                </div>
              )}

              {tags.length < 5 && (
                <div className="text-xs text-gray-500 px-1">
                  {tags.length}/5 tags • Type and press Enter
                </div>
              )}
            </div>
          </div>

          <div className="hidden sm:block">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (optional)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm border border-gray-300"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-gray-900 ml-1"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={currentTag}
              onChange={(e) => setCurrentTag(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder="Type a tag and press Enter (max 5 tags)"
              className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-800 focus:border-transparent transition shadow-sm"
              maxLength={20}
              disabled={tags.length >= 5}
            />
            <div className="text-xs text-gray-500 mt-1">
              {tags.length}/5 tags • Press Enter to add tag
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      {/* Title input */}
      <div className="mb-4 sm:mb-6">
        <div className="sm:hidden">
          {/* Mobile version with bottom border */}
          <div className="relative">
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Enter your ${postType.toLowerCase()} title...`}
              className="w-full p-4 text-base bg-transparent border-0 border-b-2 border-black focus:outline-none focus:ring-0 placeholder-gray-500"
              maxLength={30}
            />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"></div>
          </div>
          <div className="text-xs text-gray-500 mt-2 px-1">
            {title.length}/30 characters
          </div>
        </div>

        <div className="hidden sm:block">
          {/* Desktop version (unchanged) */}
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Title *
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`Enter your ${postType.toLowerCase()} title...`}
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition shadow-sm"
            maxLength={30}
          />
          <div className="text-xs text-gray-500 mt-1">
            {title.length}/30 characters
          </div>
        </div>
      </div>

      <div className="mb-4 sm:mb-6 px-4 sm:px-0">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
          Styling Options
        </label>
        <div className="flex items-center gap-2">
          {/* Font Selector - Shows "A" in selected font */}
          <button
            type="button"
            onClick={() => setShowFontModal(!showFontModal)}
            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-all"
            title="Font Style"
          >
            <span
              className={`text-xl sm:text-2xl font-bold text-gray-800 ${selectedFont}`}
            >
              A
            </span>
          </button>

          {/* Background Selector - Shows circle with selected background */}
          <button
            type="button"
            onClick={() => setShowBackgroundModal(!showBackgroundModal)}
            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-all"
            title="Background"
          >
            <div
              className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 border-gray-400 ${
                backgroundOptions[backgroundOption]?.class || "bg-white"
              }`}
            />
          </button>

          {/* Color Selector */}
          <button
            type="button"
            onClick={() => setShowColorModal(!showColorModal)}
            className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-gray-300 rounded-lg transition-all ${
              selectedColor === "white" ? "bg-black" : "bg-white"
            }`}
            title="Text Color"
          >
            <span
              className={`text-xl sm:text-2xl font-bold ${selectedFont} ${
                selectedColor === "white" ? "text-white" : "text-black"
              }`}
            >
              A
            </span>
          </button>
        </div>
      </div>

      {/* Font Modal */}
      {showFontModal && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setShowFontModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Modal Content */}
          <div
            className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-lg w-full ${
              window.innerWidth < 640
                ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Font Style
              </h3>
              <button
                onClick={() => setShowFontModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div
              className={`${
                window.innerWidth < 640
                  ? "max-h-[calc(40vh-70px)]"
                  : "max-h-[calc(60vh-80px)]"
              } overflow-y-auto`}
            >
              <div className="p-1">
                <div className="grid grid-cols-3 md:grid-cols-5 gap-1">
                  {fontOptions.map((font) => (
                    <button
                      key={font.class}
                      onClick={() => {
                        setSelectedFont(font.class);
                        setShowFontModal(false);
                      }}
                      className={`flex flex-col items-center p-3 rounded-lg transition-all relative ${
                        selectedFont === font.class
                          ? "bg-gray-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`text-3xl sm:text-4xl font-bold text-gray-800 mb-2 ${font.class}`}
                      >
                        A
                      </span>
                      <span className="text-xs text-gray-600 text-center">
                        {font.name}
                      </span>
                      {/* Selected underline */}
                      {selectedFont === font.class && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background Modal - Circle swatches */}
      {showBackgroundModal && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setShowBackgroundModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Modal Content */}
          <div
            className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-2xl w-full ${
              window.innerWidth < 640
                ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">
                Choose Background
              </h3>
              <button
                onClick={() => setShowBackgroundModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div
              className={`${
                window.innerWidth < 640
                  ? "max-h-[calc(40vh-70px)]"
                  : "max-h-[calc(60vh-80px)]"
              } overflow-y-auto`}
            >
              <div className="p-1">
                <div className="grid grid-cols-3 md:grid-cols-5 gap-1">
                  {Object.entries(backgroundOptions).map(([key, option]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setBackgroundOption(key);
                        setShowBackgroundModal(false);
                      }}
                      className={`flex flex-col items-center p-3 rounded-lg transition-all relative ${
                        backgroundOption === key
                          ? "bg-gray-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-gray-300 mb-2 ${option.class}`}
                      >
                        {key === "none" && (
                          <div className="h-full w-full rounded-full flex items-center justify-center text-lg text-gray-600 font-bold">
                            ×
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-600 text-center leading-tight">
                        {option.label}
                      </span>
                      {/* Selected underline */}
                      {backgroundOption === key && (
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Color Modal */}
      {showColorModal && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setShowColorModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Modal Content */}
          <div
            className={`absolute bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-lg w-full ${
              window.innerWidth < 640
                ? "bottom-0 left-0 right-0 max-h-[70vh] rounded-b-none animate-slide-up"
                : "top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[60vh]"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Text Color
              </h3>
              <button
                onClick={() => setShowColorModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div
              className={`${
                window.innerWidth < 640
                  ? "max-h-[calc(40vh-70px)]"
                  : "max-h-[calc(60vh-80px)]"
              } overflow-y-auto`}
            >
              <div className="p-1">
                <div className="grid grid-cols-2 gap-1">
                  <button
                    onClick={() => {
                      setSelectedColor("black");
                      setShowColorModal(false);
                    }}
                    className={`flex flex-col items-center p-4 rounded-lg transition-all relative ${
                      selectedColor === "black"
                        ? "bg-gray-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="w-12 h-12 bg-black rounded-full border-2 border-gray-300 mb-2"></div>
                    <span className="text-xs text-gray-600 text-center">
                      Black
                    </span>
                    {/* Selected underline */}
                    {selectedColor === "black" && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedColor("white");
                      setShowColorModal(false);
                    }}
                    className={`flex flex-col items-center p-4 rounded-lg transition-all relative ${
                      selectedColor === "white"
                        ? "bg-gray-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="w-12 h-12 bg-white rounded-full border-2 border-gray-300 mb-2"></div>
                    <span className="text-xs text-gray-600 text-center">
                      White
                    </span>
                    {/* Selected underline */}
                    {selectedColor === "white" && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gray-800 rounded-full"></div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content restrictions info */}
      <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle
            size={16}
            className="text-gray-600 mt-0.5 flex-shrink-0"
          />
          <div className="text-xs sm:text-sm text-gray-700">
            <p className="font-medium mb-1">Content Guidelines:</p>
            <ul className="space-y-1 list-disc list-inside text-gray-600">
              <li>Text auto-wraps at 42 characters per line</li>
              <li>Maximum 2 consecutive line breaks</li>
              <li>Current line: {lineInfo.lineLength}/42 characters</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content textarea with background */}
      <div className="mb-4 sm:mb-6">
        <div className="sm:hidden">
          {/* Mobile version with top and bottom borders */}
          <div
            className={`relative border-t-2 border-b-2 border-black overflow-hidden ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              ref={contentRef}
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleContentKeyDown}
              onSelect={handleCursorPositionChange}
              onBlur={handleCursorPositionChange}
              onClick={handleCursorPositionChange}
              placeholder={`Write your ${postType.toLowerCase()} here...\n\nTip: Text will auto-wrap at 42 characters per line, and only 2 consecutive line breaks allowed.`}
              className={`w-full p-2 text-[14px] bg-transparent focus:outline-none min-h-[200px] resize-y ${selectedFont} ${
                selectedColor === "white" ? "text-white" : "text-black"
              } placeholder-gray-500`}
              style={{ resize: "vertical" }}
            />
          </div>
        </div>

        <div className="hidden sm:block">
          {/* Desktop version (unchanged) */}
          <div
            className={`rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300 focus-within:border-gray-800 transition-colors ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              ref={contentRef}
              value={content}
              onChange={handleContentChange}
              onKeyDown={handleContentKeyDown}
              onSelect={handleCursorPositionChange}
              onBlur={handleCursorPositionChange}
              onClick={handleCursorPositionChange}
              placeholder={`Write your ${postType.toLowerCase()} here...\n\nTip: Text will auto-wrap at 42 characters per line, and only 2 consecutive line breaks allowed.`}
              className={`w-full p-4 bg-transparent focus:outline-none min-h-[300px] resize-y ${selectedFont} ${
                selectedColor === "white" ? "text-white" : "text-black"
              } placeholder-gray-400 text-base`}
              style={{ resize: "vertical" }}
            />
          </div>
        </div>
      </div>
      {/* Tags */}
      <div className="mb-4 sm:mb-6 px-4 sm:px-0">
        <div className="sm:hidden">
          {/* Mobile version - Compact */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Tags display inline with input */}
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm border border-gray-300 flex-shrink-0"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}

              {/* Input when there's space */}
              {tags.length < 5 && (
                <input
                  id="tags"
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add tag..."
                  className="flex-1 min-w-[100px] p-2 text-sm bg-transparent border-0 focus:outline-none placeholder-gray-400"
                  maxLength={20}
                />
              )}
            </div>

            {tags.length >= 5 && (
              <div className="text-xs text-gray-500 px-1">
                Maximum 5 tags reached
              </div>
            )}

            {tags.length < 5 && (
              <div className="text-xs text-gray-500 px-1">
                {tags.length}/5 tags • Type and press Enter
              </div>
            )}
          </div>
        </div>

        <div className="hidden sm:block">
          {/* Desktop version (unchanged) */}
          <label
            htmlFor="tags"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Tags (optional)
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm border border-gray-300"
              >
                #{tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-gray-900 ml-1"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <input
            id="tags"
            type="text"
            value={currentTag}
            onChange={(e) => setCurrentTag(e.target.value)}
            onKeyDown={handleTagKeyDown}
            placeholder="Type a tag and press Enter (max 5 tags)"
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-800 focus:border-transparent transition shadow-sm"
            maxLength={20}
            disabled={tags.length >= 5}
          />
          <div className="text-xs text-gray-500 mt-1">
            {tags.length}/5 tags • Press Enter to add tag
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PostForm;